import React, { useState } from 'react';
import { OrganizerGuidesList } from '../components/guides/OrganizerGuidesList';
import { GuidesList } from '../components/guides/GuidesList';
import { UserCog, Plus, Download, FileImage } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AddGuideModal } from '../components/guides/AddGuideModal';
import { motion } from 'framer-motion';
import { useGuidesStore } from '../stores/guidesStore';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export const GuidesPage = () => {
  const userRole = localStorage.getItem('userRole');
  const { translations } = useLanguage();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { guides } = useGuidesStore();

  const downloadAllPassportPhotos = async () => {
    try {
      // Get all guides with passport photos
      const guidesWithPhotos = Object.values(guides).filter(guide => guide.passportPhotoUrl);
      
      if (guidesWithPhotos.length === 0) {
        toast.error('No passport photos to download');
        return;
      }
      
      setIsDownloading(true);
      toast.loading('Preparing all passport photos for download...', { id: 'download-all-zip' });
      
      // Create a new zip file
      const zip = new JSZip();
      
      // Track progress
      let processedCount = 0;
      
      // Process photos in small batches to avoid memory issues
      const batchSize = 5;
      
      for (let i = 0; i < guidesWithPhotos.length; i += batchSize) {
        const batch = guidesWithPhotos.slice(i, i + batchSize);
        
        // Process each guide's photo in parallel within the batch
        await Promise.all(batch.map(async (guide) => {
          try {
            const response = await fetch(guide.passportPhotoUrl!);
            
            if (!response.ok) {
              console.error(`Failed to fetch photo for ${guide.name}: ${response.statusText}`);
              return;
            }
            
            const blob = await response.blob();
            
            // Use passport number or name for filename
            const filename = guide.passportNumber ? 
              `${guide.passportNumber}.${guide.passportPhotoUrl!.split('.').pop()}` :
              `${guide.name.replace(/\s+/g, '_')}.${guide.passportPhotoUrl!.split('.').pop()}`;
            
            // Add file to zip
            zip.file(filename, blob);
            
            // Update progress
            processedCount++;
            const progress = Math.round((processedCount / guidesWithPhotos.length) * 100);
            toast.loading(`Processing: ${progress}%`, { id: 'download-all-zip' });
          } catch (error) {
            console.error(`Error processing photo for ${guide.name}:`, error);
          }
        }));
      }
      
      // Generate zip file
      toast.loading('Generating zip file...', { id: 'download-all-zip' });
      const content = await zip.generateAsync({ type: 'blob' });
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `all_passport_photos_${date}.zip`;
      
      // Save the zip file
      saveAs(content, filename);
      
      toast.dismiss('download-all-zip');
      toast.success(`Downloaded ${processedCount} passport photos`);
    } catch (error) {
      console.error('Error downloading all photos:', error);
      toast.dismiss('download-all-zip');
      toast.error('Failed to download passport photos');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <UserCog className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.guidesPage?.title || 'Guides'}
            </h1>
            <p className="text-gray-500 mt-1">
              {userRole === 'organizer'
                ? translations?.guidesPage?.organizerDescription ||
                  'Manage your assigned guides.'
                : translations?.guidesPage?.adminDescription ||
                  'Manage all guides in the system.'}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          {/* Download All Passport Photos Button */}
          {userRole === 'admin' && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadAllPassportPhotos}
              disabled={isDownloading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 
                        transition-colors flex items-center gap-2"
            >
              {isDownloading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <FileImage size={20} />
                  <span>Download All Passports</span>
                </>
              )}
            </motion.button>
          )}

          {userRole === 'admin' && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 
                        transition-colors flex items-center gap-2"
            >
              <Plus size={20} />
              <span>
                {translations?.guidesPage?.addGuideButton || 'Add Guide'}
              </span>
            </button>
          )}
        </div>
      </div>

      {userRole === 'organizer' ? <OrganizerGuidesList /> : <GuidesList />}

      <AddGuideModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  );
};